import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { listChallenges, createChallenge, updateChallenge, removeChallenge } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, Flag } from "lucide-react";

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const shift = (month, n) => { const [y, m] = month.split("-").map(Number); return ym(new Date(y, m - 1 + n, 1)); };
const label = (month) => new Date(`${month}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });

export default function MonthlyChallenges() {
  const { activeFacility, effectiveRole, user } = useAuth();
  const isManager = atLeast(effectiveRole || user?.role, "manager");
  const [month, setMonth] = useState(() => ym(new Date()));
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await listChallenges(month));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load challenges");
      setData({ challenges: [], metrics: {} });
    }
  }, [month]);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const metrics = useMemo(() => Object.entries(data?.metrics || {}), [data]);

  const save = async () => {
    const f = editing;
    const body = { month, scope: f.scope, title: f.title.trim(), description: (f.description || "").trim() || null,
                   metric: f.metric, target: Number(f.target), xp: Number(f.xp) };
    if (body.title.length < 3) { toast.error("Give it a title"); return; }
    setSaving(true);
    try {
      if (f.id) await updateChallenge(f.id, body); else await createChallenge(body);
      toast.success("Challenge saved. Members see it on Home.");
      setEditing(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Check the target and XP" : d || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove "${c.title}"? Members who already finished it keep their XP and badge.`)) return;
    try {
      await removeChallenge(c.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    }
  };

  const canEdit = (c) => isManager && (c.scope === "facility" || data?.can_edit_all);

  return (
    <div className="space-y-6" data-testid="monthly-challenges">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Member App · Engagement</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Monthly Challenges</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Goals for the month, shown on every member&apos;s Home with their progress. Finishing one pays XP and a limited badge.
            If nothing is set for a month, three defaults run for every gym. Seasons (each quarter) run automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonth(shift(month, -1))}><ChevronLeft className="h-4 w-4" /></Button>
          <div className="min-w-[150px] text-center font-semibold">{label(month)}</div>
          <Button variant="outline" size="icon" onClick={() => setMonth(shift(month, 1))}><ChevronRight className="h-4 w-4" /></Button>
          {isManager && (
            <Button onClick={() => setEditing({ scope: "facility", title: "", description: "", metric: "sessions", target: 12, xp: 250 })} data-testid="challenge-add">
              <Plus className="mr-1 h-4 w-4" /> Add challenge
            </Button>
          )}
        </div>
      </div>

      {data === null ? <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.challenges.map((c) => (
            <div key={c.id} className="card-surface p-5" data-testid={`challenge-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Flag className="h-4 w-4 text-[#ff6b70]" />
                  <div className="font-semibold text-zinc-100">{c.title}</div>
                </div>
                {canEdit(c) && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing({ ...c })}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(c)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
              </div>
              {c.description && <p className="mt-1 text-sm text-zinc-400">{c.description}</p>}
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                <span>{data.metrics[c.metric]}: <b className="text-zinc-300">{Number(c.target).toLocaleString()}</b></span>
                <span>+{c.xp} XP</span>
                <span>{c.scope === "all" ? "Every gym" : activeFacility?.name || "This gym"}</span>
                <span className="text-emerald-300">{c.completed} completed</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit challenge" : `New challenge for ${label(month)}`}</DialogTitle>
            <DialogDescription>Progress is counted automatically from the app and gym check-ins.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <label className="block space-y-1"><span className="text-xs text-zinc-400">Title</span>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="20 sessions in November" /></label>
              <label className="block space-y-1"><span className="text-xs text-zinc-400">Description (optional)</span>
                <Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Check in at the gym 20 times this month." /></label>
              <div className="grid grid-cols-3 gap-3">
                <label className="col-span-3 block space-y-1"><span className="text-xs text-zinc-400">What counts</span>
                  <select className="w-full rounded-md border border-zinc-700 bg-transparent px-3 py-2 text-sm" value={editing.metric}
                    onChange={(e) => setEditing({ ...editing, metric: e.target.value })}>
                    {metrics.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select></label>
                <label className="block space-y-1"><span className="text-xs text-zinc-400">Target</span>
                  <Input type="number" min={1} value={editing.target} onChange={(e) => setEditing({ ...editing, target: e.target.value })} /></label>
                <label className="block space-y-1"><span className="text-xs text-zinc-400">XP reward</span>
                  <Input type="number" min={10} value={editing.xp} onChange={(e) => setEditing({ ...editing, xp: e.target.value })} /></label>
                <label className="block space-y-1"><span className="text-xs text-zinc-400">For</span>
                  <select className="w-full rounded-md border border-zinc-700 bg-transparent px-3 py-2 text-sm" value={editing.scope}
                    onChange={(e) => setEditing({ ...editing, scope: e.target.value })} disabled={!data?.can_edit_all}>
                    <option value="facility">This gym</option>
                    {data?.can_edit_all && <option value="all">Every gym</option>}
                  </select></label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
